// src/pages/MyPaymentsPage.jsx
// LOOP CLIENT PAYMENT HISTORY V73
// Unified read-only client history for paid bookings + membership payments.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  CalendarDays,
  ChevronRight,
  CreditCard,
  Crown,
  Receipt,
  RefreshCw,
  Store,
  X,
} from "lucide-react";
import { callCallableNativeSafe } from "../utils/callableNativeSafe";
import { downloadPaymentReceipt } from "../utils/bookingPaymentReceipt";

function money(value, currency = "USD") {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("es-PA", {
      style: "currency",
      currency: String(currency || "USD").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch (_) {
    return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
}

function fmtDate(ms, withTime = false) {
  const value = Number(ms || 0);
  if (!(value > 0)) return "Fecha no disponible";

  try {
    return new Intl.DateTimeFormat("es-PA", withTime
      ? { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }
      : { day: "numeric", month: "short", year: "numeric" }
    ).format(new Date(value));
  } catch (_) {
    return new Date(value).toLocaleDateString();
  }
}

function sourceLabel(payment = {}) {
  if (payment.type === "membership_renewal") return "Renovaci\u00f3n de membres\u00eda";
  if (payment.source === "membership") return "Membres\u00eda";
  return "Reserva";
}

function methodLabel(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "No disponible";

  const lower = raw.toLowerCase();
  if (lower === "cash" || lower === "efectivo") return "Efectivo";
  if (lower === "card" || lower === "credit_card" || lower === "debit_card") return "Tarjeta";
  if (lower === "wompi") return "Tarjeta online";
  if (lower === "yappy") return "Yappy";
  return raw;
}

function statusLabel(value = "") {
  return String(value || "").toLowerCase() === "refunded" ? "Reembolsado" : "Pagado";
}

function PaymentDetailModal({ payment, onClose }) {
  const [receiptError,setReceiptError] = useState("");
  useEffect(() => {setReceiptError("");},[payment]);
  useEffect(() => { if (!payment) return; const close = event => {if(event.key === "Escape") onClose();}; window.addEventListener("keydown",close); return () => window.removeEventListener("keydown",close); },[payment,onClose]);
  if (!payment || typeof document === "undefined") return null;

  const modal = (
    <div className="mpp-modal-layer" role="dialog" aria-modal="true" aria-label="Detalle del pago">
      <button type="button" className="mpp-modal-backdrop" aria-label="Cerrar" onClick={onClose} />
      <section className="mpp-modal">
        <button type="button" className="mpp-modal-close" aria-label="Cerrar" onClick={onClose}>
          <X size={19} />
        </button>

        <div className="mpp-modal-head">
          <span className="mpp-modal-icon">
            {payment.source === "membership" ? <Crown size={21} /> : <Receipt size={21} />}
          </span>
          <div>
            <span>DETALLE DEL PAGO</span>
            <h3>{payment.concept || sourceLabel(payment)}</h3>
            <p><Store size={13} /> {payment.businessName || "Negocio"}</p>
          </div>
          <em className={`mpp-status ${payment.status === "refunded" ? "is-refunded" : ""}`}>
            {statusLabel(payment.status)}
          </em>
        </div>

        <div className="mpp-detail-amount">
          <span>Monto</span>
          <strong>{money(payment.amount, payment.currency)}</strong>
        </div>

        <div className="mpp-detail-grid">
          <div>
            <span>Concepto</span>
            <strong>{sourceLabel(payment)}</strong>
          </div>
          <div>
            <span>Fecha</span>
            <strong>{fmtDate(payment.paidAtMs, true)}</strong>
          </div>
          <div>
            <span>{"M\u00e9TODO"}</span>
            <strong>{methodLabel(payment.method)}</strong>
          </div>
          <div>
            <span>Estado</span>
            <strong>{statusLabel(payment.status)}</strong>
          </div>
        </div>

        {receiptError && <p role="alert">{receiptError}</p>}
        {payment.reference ? (
          <div className="mpp-reference">
            <span>Referencia</span>
            <strong>{payment.reference}</strong>
          </div>
        ) : null}

        <p className="mpp-detail-note">
          Este historial refleja pagos registrados en Loop. No muestra datos completos de tu tarjeta.
        </p>
        {["booking","membership"].includes(payment.source) && Number(payment.amount) > 0 ? (
          <button className="mpp-receipt-button" type="button" onClick={() => { try { downloadPaymentReceipt(payment); } catch (_) { setReceiptError("No pudimos preparar el comprobante. Intenta nuevamente."); } }}>
            Descargar comprobante
          </button>
        ) : null}
      </section>
    </div>
  );

  return createPortal(modal, document.body);
}

export default function MyPaymentsPage({ embedded = false } = {}) {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ count: 0, bookings: 0, memberships: 0 });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const res = await callCallableNativeSafe({
        functionName: "getMyPaymentHistory",
        data: { limit: 100 },
        timeoutMs: 30000,
      });

      const rows = Array.isArray(res?.data?.payments) ? res.data.payments : [];
      setPayments(rows);
      setSummary(res?.data?.summary || {
        count: rows.length,
        bookings: rows.filter((x) => x.source === "booking").length,
        memberships: rows.filter((x) => x.source === "membership").length,
      });
    } catch (e) {
      console.warn("[MyPaymentsPage] payment history failed", e);
      setError("No pudimos cargar tu historial de pagos. Intenta nuevamente.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const normalize = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    return payments.filter(x => (filter === "all" || x.source === (filter === "bookings" ? "booking" : "membership")) && normalize([x.concept,x.businessName,x.reference].join(" ")).includes(normalize(search)));
  }, [payments, filter, search]);

  const latest = payments[0] || null;

  return (
    <section className={`mpp-page ${embedded ? "is-embedded" : ""}`}>
      <style>{styles}</style>

      <div className="mpp-toolbar"><label className="mpp-search"><span>Buscar pago</span><input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Negocio, concepto o referencia" /></label>
        <div className="mpp-filters" role="group" aria-label="Filtrar pagos">
          <button type="button" aria-pressed={filter === "all"} className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
            Todos <span>{payments.length}</span>
          </button>
          <button type="button" aria-pressed={filter === "bookings"} className={filter === "bookings" ? "active" : ""} onClick={() => setFilter("bookings")}>
            Reservas <span>{summary.bookings || 0}</span>
          </button>
          <button type="button" aria-pressed={filter === "memberships"} className={filter === "memberships" ? "active" : ""} onClick={() => setFilter("memberships")}>
            {"Membres\u00edas"} <span>{summary.memberships || 0}</span>
          </button>
        </div>
        <button type="button" className="mpp-refresh" aria-label="Actualizar pagos" title="Actualizar pagos" disabled={loading || refreshing} onClick={() => load({silent:true})}><RefreshCw size={18} className={refreshing ? "is-spinning" : ""}/><span className="mpp-refresh-label">{refreshing ? "Actualizando…" : "Actualizar"}</span></button>
      </div>

      {loading ? (
        <div className="mpp-loading">
          <span />
          <span />
          <span />
        </div>
      ) : error ? (
        <div className="mpp-empty is-error">
          <Receipt size={22} />
          <strong>No pudimos cargar tus pagos</strong>
          <p>{error}</p>
          <button type="button" onClick={() => load()}>Intentar nuevamente</button>
        </div>
      ) : filtered.length ? (
        <div className="mpp-list">
          {filtered.map((payment) => (
            <button
              type="button"
              className="mpp-row"
              key={payment.id}
              onClick={() => setSelected(payment)}
            >
              <span className={`mpp-row-icon ${payment.source === "membership" ? "is-membership" : ""}`}>
                {payment.source === "membership" ? <Crown size={18} /> : <Receipt size={18} />}
              </span>

              <span className="mpp-row-main">
                <span>{sourceLabel(payment)}</span>
                <strong>{payment.concept || sourceLabel(payment)}</strong>
                <small><Store size={12} /> {payment.businessName || "Negocio"}</small>
              </span>

              <span className="mpp-row-date">
                <CalendarDays size={13} />
                {fmtDate(payment.paidAtMs, true)}
              </span>

              <span className="mpp-row-money">
                <strong>{money(payment.amount, payment.currency)}</strong>
                <em className={payment.status === "refunded" ? "is-refunded" : ""}>
                  {statusLabel(payment.status)}
                </em>
              </span>

              <ChevronRight size={18} className="mpp-row-arrow" />
            </button>
          ))}
        </div>
      ) : (
        <div className="mpp-empty">
          <CreditCard size={24} />
          <strong>{search ? "No encontramos pagos con esa búsqueda" : filter === "all" ? "Todav\u00eda no tienes pagos registrados" : "No hay pagos en este filtro"}</strong>
          <p>{"Cuando tengas pagos de reservas o membres\u00edas, aparecer\u00e1n aqu\u00ed."}</p>
        </div>
      )}

      <p className="mpp-history-note">Hasta 100 movimientos recientes. Los comprobantes no sustituyen una factura fiscal.</p>
      <PaymentDetailModal payment={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

const styles = `
  .mpp-page{
    width:100%;
    color:#142235;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
  }

  .mpp-head{
    display:flex;
    align-items:flex-end;
    justify-content:space-between;
    gap:18px;
    margin-bottom:11px;
  }

  .mpp-head > div > span{
    display:block;
    margin-bottom:3px;
    color:#00776d;
    font-size:9.5px;
    font-weight:950;
    letter-spacing:.14em;
  }

  .mpp-head h2{
    margin:0;
    color:#0f1d2f;
    font-size:24px;
    line-height:1;
    letter-spacing:-.04em;
  }

  .mpp-head p{
    margin:2px 0 0;
    color:#718096;
    font-size:11px;
    line-height:1.35;
    font-weight:650;
  }

  .mpp-refresh{
    min-height:38px;
    padding:0 13px;
    border:1px solid #d9e5e5;
    border-radius:999px;
    background:#fff;
    color:#00776d;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    gap:7px;
    font:inherit;
    font-size:11px;
    font-weight:850;
    cursor:pointer;
  }

  .mpp-refresh:disabled{opacity:.58;cursor:default;}
  .mpp-refresh .is-spinning{animation:mppSpin .8s linear infinite;}
  @keyframes mppSpin{to{transform:rotate(360deg);}}

  .mpp-summary{
    display:grid;
    grid-template-columns:repeat(2,minmax(0,1fr));
    gap:8px;
    margin-bottom:10px;
  }

  .mpp-summary > div{
    min-height:62px;
    padding:10px 13px;
    border:1px solid #dfe8ed;
    border-radius:15px;
    background:linear-gradient(145deg,#f9fbfd,#fff);
    display:grid;
    align-content:center;
    grid-template-columns:auto 1fr;
    column-gap:10px;
    row-gap:2px;
  }

  .mpp-summary span{
    grid-column:1 / -1;
    color:#8391a5;
    font-size:8px;
    font-weight:900;
    letter-spacing:.08em;
    text-transform:uppercase;
  }

  .mpp-summary strong{
    color:#13263a;
    font-size:15px;
    line-height:1.05;
  }

  .mpp-summary small{
    align-self:end;
    color:#728095;
    font-size:9px;
    font-weight:650;
  }

  .mpp-toolbar{
    display:flex;
    align-items:center;
    justify-content:space-between;
    margin-bottom:8px;
  }

  .mpp-filters{
    display:flex;
    gap:7px;
    overflow-x:auto;
    scrollbar-width:none;
  }

  .mpp-filters::-webkit-scrollbar{display:none;}

  .mpp-filters button{
    min-height:34px;
    padding:0 11px;
    border:1px solid #dfe7ec;
    border-radius:999px;
    background:#fff;
    color:#5e6f83;
    display:inline-flex;
    align-items:center;
    gap:6px;
    font:inherit;
    font-size:10.5px;
    font-weight:800;
    white-space:nowrap;
    cursor:pointer;
  }

  .mpp-filters button span{
    min-width:19px;
    height:19px;
    padding:0 5px;
    border-radius:999px;
    background:#f1f5f7;
    display:inline-grid;
    place-items:center;
    font-size:9px;
  }

  .mpp-filters button.active{
    border-color:#00776d;
    background:#eaf9f6;
    color:#00776d;
  }

  .mpp-list{
    overflow:hidden;
    border:1px solid #dfe7eb;
    border-radius:18px;
    background:#fff;
  }

  .mpp-row{
    width:100%;
    min-height:78px;
    padding:12px 13px;
    border:0;
    border-bottom:1px solid #edf1f3;
    background:#fff;
    display:grid;
    grid-template-columns:38px minmax(180px,1fr) 132px 112px 22px;
    align-items:center;
    gap:11px;
    color:inherit;
    font:inherit;
    text-align:left;
    cursor:pointer;
    transition:background 140ms ease;
  }

  .mpp-row:last-child{border-bottom:0;}
  .mpp-row:hover{background:#f9fcfb;}

  .mpp-row-icon{
    width:38px;
    height:38px;
    border-radius:12px;
    display:grid;
    place-items:center;
    background:#f0f5f7;
    color:#60778a;
  }

  .mpp-row-icon.is-membership{
    background:#eaf9f6;
    color:#00776d;
  }

  .mpp-row-main{
    min-width:0;
    display:grid;
    gap:2px;
  }

  .mpp-row-main > span{
    color:#7e8ca0;
    font-size:8.5px;
    line-height:1;
    font-weight:900;
    text-transform:uppercase;
    letter-spacing:.08em;
  }

  .mpp-row-main strong{
    min-width:0;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
    color:#15263a;
    font-size:12.5px;
  }

  .mpp-row-main small{
    min-width:0;
    display:flex;
    align-items:center;
    gap:4px;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
    color:#728095;
    font-size:9.5px;
    font-weight:650;
  }

  .mpp-row-date{
    color:#68798c;
    display:flex;
    align-items:center;
    gap:5px;
    font-size:10px;
    font-weight:700;
  }

  .mpp-row-money{
    display:grid;
    justify-items:end;
    gap:4px;
  }

  .mpp-row-money strong{
    color:#13263a;
    font-size:12.5px;
  }

  .mpp-row-money em,
  .mpp-status{
    padding:5px 8px;
    border-radius:999px;
    background:#e6f7eb;
    color:#14804a;
    font-size:8.5px;
    line-height:1;
    font-weight:900;
    font-style:normal;
  }

  .mpp-row-money em.is-refunded,
  .mpp-status.is-refunded{
    background:#fff4df;
    color:#a56300;
  }

  .mpp-row-arrow{color:#94a3b8;}

  .mpp-empty{
    min-height:190px;
    padding:28px;
    border:1px dashed #d6e2e5;
    border-radius:18px;
    background:#fbfdfd;
    display:grid;
    place-items:center;
    align-content:center;
    gap:7px;
    color:#6e7f92;
    text-align:center;
  }

  .mpp-empty strong{color:#1d3144;font-size:13px;}
  .mpp-empty p{max-width:390px;margin:0;font-size:10.5px;line-height:1.45;font-weight:650;}
  .mpp-empty button{
    margin-top:5px;
    min-height:36px;
    padding:0 13px;
    border:0;
    border-radius:999px;
    background:#00776d;
    color:#fff;
    font:inherit;
    font-size:10.5px;
    font-weight:850;
    cursor:pointer;
  }

  .mpp-loading{display:grid;gap:8px;}
  .mpp-loading span{
    display:block;
    height:76px;
    border-radius:17px;
    background:linear-gradient(90deg,#f1f5f6,#f8fbfb,#f1f5f6);
    background-size:200% 100%;
    animation:mppShimmer 1.2s linear infinite;
  }
  @keyframes mppShimmer{to{background-position:-200% 0;}}

  .mpp-modal-layer{
    position:fixed;
    inset:0;
    z-index:10080;
    display:grid;
    place-items:center;
    padding:18px;
  }

  .mpp-modal-backdrop{
    position:absolute;
    inset:0;
    border:0;
    background:rgba(13,27,37,.45);
    backdrop-filter:blur(7px);
    -webkit-backdrop-filter:blur(7px);
  }

  .mpp-modal{
    position:relative;
    z-index:1;
    width:min(560px,calc(100vw - 30px));
    padding:20px;
    border:1px solid #dfe7e9;
    border-radius:24px;
    background:#fff;
    box-shadow:0 30px 90px rgba(15,23,42,.22);
  }

  .mpp-modal-close{
    position:absolute;
    top:14px;
    right:14px;
    width:38px;
    height:38px;
    border:0;
    border-radius:999px;
    background:#f7f9fa;
    color:#17283a;
    display:grid;
    place-items:center;
    cursor:pointer;
  }

  .mpp-modal-head{
    padding-right:46px;
    display:grid;
    grid-template-columns:46px minmax(0,1fr) auto;
    align-items:center;
    gap:11px;
  }

  .mpp-modal-icon{
    width:46px;
    height:46px;
    border-radius:15px;
    background:#e9f8f5;
    color:#00776d;
    display:grid;
    place-items:center;
  }

  .mpp-modal-head > div{
    min-width:0;
    display:grid;
    gap:2px;
  }

  .mpp-modal-head > div > span{
    color:#00776d;
    font-size:8px;
    font-weight:950;
    letter-spacing:.12em;
  }

  .mpp-modal-head h3{
    margin:0;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
    color:#122338;
    font-size:18px;
    letter-spacing:-.03em;
  }

  .mpp-modal-head p{
    margin:0;
    display:flex;
    align-items:center;
    gap:4px;
    color:#718095;
    font-size:10px;
    font-weight:650;
  }

  .mpp-detail-amount{
    margin-top:17px;
    padding:15px;
    border-radius:17px;
    background:#eaf9f6;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:12px;
  }

  .mpp-detail-amount span{
    color:#39766f;
    font-size:10px;
    font-weight:800;
  }

  .mpp-detail-amount strong{
    color:#006d64;
    font-size:22px;
    letter-spacing:-.03em;
  }

  .mpp-detail-grid{
    display:grid;
    grid-template-columns:repeat(2,minmax(0,1fr));
    gap:9px;
    margin-top:10px;
  }

  .mpp-detail-grid > div,
  .mpp-reference{
    padding:12px;
    border:1px solid #e1e8ec;
    border-radius:14px;
    background:#fafcfd;
    display:grid;
    gap:3px;
  }

  .mpp-detail-grid span,
  .mpp-reference span{
    color:#8592a4;
    font-size:8.5px;
    font-weight:900;
    text-transform:uppercase;
    letter-spacing:.06em;
  }

  .mpp-detail-grid strong,
  .mpp-reference strong{
    color:#233649;
    font-size:10.5px;
    overflow-wrap:anywhere;
  }

  .mpp-reference{margin-top:9px;}

  .mpp-detail-note{
    margin:12px 2px 0;
    color:#7a899b;
    font-size:9.5px;
    line-height:1.45;
    font-weight:650;
  }

  @media(max-width:760px){
    .mpp-head{align-items:flex-start;flex-direction:column;}
    .mpp-refresh{align-self:flex-start;}
    .mpp-row{
      grid-template-columns:38px minmax(0,1fr) auto 18px;
      min-height:76px;
    }
    .mpp-row-date{display:none;}
    .mpp-row-money{grid-column:3;grid-row:1;}
    .mpp-row-arrow{grid-column:4;grid-row:1;}
  }

  @media(max-width:520px){
    .mpp-summary{grid-template-columns:1fr 1fr;gap:7px;}
    .mpp-summary > div{min-height:60px;padding:9px 10px;}
    .mpp-summary strong{font-size:16px;}
    .mpp-row{padding:11px 9px;gap:8px;}
    .mpp-row-main strong{font-size:11.5px;}
    .mpp-row-money strong{font-size:11px;}
    .mpp-modal-layer{align-items:end;padding:0;}
    .mpp-modal{
      width:100%;
      max-height:88vh;
      overflow:auto;
      border-radius:24px 24px 0 0;
      padding:18px;
    }
    .mpp-modal-head{grid-template-columns:44px minmax(0,1fr);padding-right:42px;}
    .mpp-modal-head .mpp-status{grid-column:2;justify-self:start;margin-top:4px;}
    .mpp-detail-grid{grid-template-columns:1fr 1fr;}
  }
`;
